// MeshCore "Add Contact" share link (issue #35). No imports on purpose: the
// frontend test runner compiles each src/lib module on its own.
//
// Format, from MeshCore's own docs (meshcore-dev/MeshCore, docs/qr_codes.md,
// "Add Contact", lines 20-36; also https://docs.meshcore.io/qr_codes/):
//   meshcore://contact/add?name=<name>&public_key=<64 hex>&type=<1-4>
// name is URL-encoded (the example writes a space as "+"), public_key is the
// 32-byte key as 64 hex characters, type is 1 Companion, 2 Repeater,
// 3 Room Server, 4 Sensor. The QR code encodes this URL as plain text.

export const CONTACT_ADD_BASE = "meshcore://contact/add";

// Keyed by the app's own MeshCore role key (definitions/protocols.ts,
// normalizeMeshCoreRole). Roles with no MeshCore type code (observer, other)
// are deliberately absent: no code is guessed for them.
export const CONTACT_TYPE_CODE: Record<string, number> = {
  companion: 1,
  repeater: 2,
  room_server: 3,
  sensor: 4,
};

export function contactTypeCode(roleKey: string | null | undefined): number | null {
  if (!roleKey) return null;
  return Object.prototype.hasOwnProperty.call(CONTACT_TYPE_CODE, roleKey) ? CONTACT_TYPE_CODE[roleKey] : null;
}

// True only for a full 32-byte key (64 hex). A 12-hex prefix is not enough
// to add a contact.
export function isFullPublicKey(key: string | null | undefined): boolean {
  return typeof key === "string" && /^[0-9a-fA-F]{64}$/.test(key.trim());
}

// Form-style escaping like the docs example: UTF-8 percent-encoding with a
// space written as "+". A literal "+" becomes %2B. Lone surrogates (which
// encodeURIComponent rejects) are replaced with U+FFFD first.
export function encodeContactName(name: string): string {
  let clean = "";
  for (let i = 0; i < name.length; i += 1) {
    const c = name.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const d = name.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        clean += name[i] + name[i + 1];
        i += 1;
      } else {
        clean += "�";
      }
    } else if (c >= 0xdc00 && c <= 0xdfff) {
      clean += "�";
    } else {
      clean += name[i];
    }
  }
  return encodeURIComponent(clean).replace(/%20/g, "+");
}

export type ContactShare =
  | { ok: true; url: string }
  | { ok: false; reason: string };

export function buildContactShare(input: {
  name: string | null | undefined;
  publicKey: string | null | undefined;
  roleKey: string | null | undefined;
}): ContactShare {
  const key = (input.publicKey ?? "").trim();
  if (!key) return { ok: false, reason: "The full public key for this node is not known yet." };
  if (!isFullPublicKey(key)) return { ok: false, reason: "Only part of this node's public key is known, so a contact link cannot be made." };
  const type = contactTypeCode(input.roleKey);
  if (type == null) return { ok: false, reason: "This node's role is not one the MeshCore app can add as a contact." };
  const name = (input.name ?? "").trim();
  if (!name) return { ok: false, reason: "This node has not advertised a name." };
  return {
    ok: true,
    url: `${CONTACT_ADD_BASE}?name=${encodeContactName(name)}&public_key=${key.toLowerCase()}&type=${type}`,
  };
}
